/* Copyright (c) 2020-present CloudIO Inc */

import { atom, useStateXGetter, useStateXSetter } from '@cloudio-saas/statex';
import useAnimationFrame from './useAnimationFrame';
import { useEffect, useState } from 'react';

const windowSizeAtom = atom({
  path: ['root', 'window', 'size'],
  defaultValue: { width: window.innerWidth, height: window.innerHeight },
});

export const rootPageWidthAtom = atom({
  path: ['root', 'page', 'width'],
  defaultValue: 0,
});

export const rootPageAtom = atom({
  path: ['root', 'page', 'pageId'],
  defaultValue: { pageId: '', appUid: '' },
});

const windowWidthAtom = atom({
  path: ['root', 'window', 'size', 'width'],
  defaultValue: window.innerWidth,
});

/**
 * Use this hook to get the current browser window size.
 * @returns [width, height] of the browser window
 */
function useWindowSize(): [number, number] {
  const set = useStateXSetter();
  const get = useStateXGetter();

  const [windowSize, setWindowSize] = useState<[number, number]>([
    window.innerWidth,
    window.innerHeight,
  ]);

  const debounce = useAnimationFrame(() => {
    setWindowSize((size) => {
      let newSize = size;
      if (size[0] !== window.innerWidth || size[1] !== window.innerHeight) {
        newSize = [window.innerWidth, window.innerHeight];
      }
      return newSize;
    });
  });

  useEffect(() => {
    const sizeValueInPath = get(windowSizeAtom);
    if (
      !sizeValueInPath ||
      sizeValueInPath.width !== windowSize[0] ||
      sizeValueInPath.height !== windowSize[1]
    ) {
      set(windowSizeAtom, { width: windowSize[0], height: windowSize[1] });
    }
  }, [windowSize, set, get]);

  useEffect(() => {
    window.addEventListener('resize', debounce);
    return () => window.removeEventListener('resize', debounce);
  }, [debounce, set]);

  return windowSize;
}

export { windowSizeAtom, windowWidthAtom, useWindowSize as default };
